/**
 * Persist staged email-theme Customize (colors + logos) per store.
 * Preview/Apply only; never sent to the notification API.
 */

import { DEFAULT_LOGO_SETTINGS } from "../constants/logos";
import { EMAIL_SKINS } from "../constants/skins";
import { THEME_DRAFT_STORAGE_PREFIX } from "../constants/themeDraft";
import { mergeLogoSettings } from "./logoTokens";

const BUILTIN_SKIN_IDS = new Set(EMAIL_SKINS.map(skin => skin.id));

function storageKey(accountId, storeId) {
    return `${THEME_DRAFT_STORAGE_PREFIX}-${accountId}-${storeId}`;
}

/** Previous key used colons; read once and move to hyphens. */
function legacyStorageKey(accountId, storeId) {
    return `${THEME_DRAFT_STORAGE_PREFIX}:${accountId}:${storeId}`;
}

function canUseStorage() {
    return typeof window !== "undefined" && window.localStorage;
}

export function isBuiltinEmailSkinId(skinId) {
    return BUILTIN_SKIN_IDS.has(skinId);
}

/**
 * @param {object | null | undefined} raw
 * @returns {object | null}
 */
export function normalizeThemeDraft(raw) {
    if (!raw || typeof raw !== "object") return null;
    const baseSkinId = isBuiltinEmailSkinId(raw.baseSkinId)
        ? raw.baseSkinId
        : "brand";
    const overrides =
        raw.overrides && typeof raw.overrides === "object" ? raw.overrides : {};
    return {
        baseSkinId,
        overrides,
        logos: mergeLogoSettings(raw.logos),
        updatedAt: raw.updatedAt || Date.now(),
    };
}

export function getThemeDraft(accountId, storeId) {
    if (!accountId || !storeId || !canUseStorage()) return null;
    try {
        const key = storageKey(accountId, storeId);
        let raw = window.localStorage.getItem(key);
        if (!raw) {
            const legacyKey = legacyStorageKey(accountId, storeId);
            raw = window.localStorage.getItem(legacyKey);
            if (raw) {
                window.localStorage.setItem(key, raw);
                window.localStorage.removeItem(legacyKey);
            }
        }
        if (!raw) return null;
        return normalizeThemeDraft(JSON.parse(raw));
    } catch {
        return null;
    }
}

export function setThemeDraft(accountId, storeId, draft) {
    const normalized = normalizeThemeDraft(draft);
    if (!accountId || !storeId || !normalized || !canUseStorage()) {
        return normalized;
    }
    try {
        window.localStorage.setItem(
            storageKey(accountId, storeId),
            JSON.stringify(normalized),
        );
        window.localStorage.removeItem(legacyStorageKey(accountId, storeId));
    } catch {
        /* quota / private mode — keep in-memory draft */
    }
    return normalized;
}

export function clearThemeDraft(accountId, storeId) {
    if (!accountId || !storeId || !canUseStorage()) return;
    try {
        window.localStorage.removeItem(storageKey(accountId, storeId));
        window.localStorage.removeItem(legacyStorageKey(accountId, storeId));
    } catch {
        /* ignore */
    }
}

export function emptyThemeDraft(baseSkinId = "brand") {
    return normalizeThemeDraft({
        baseSkinId,
        overrides: {},
        logos: DEFAULT_LOGO_SETTINGS,
        updatedAt: Date.now(),
    });
}
